import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { useStore } from '../lib/store';
import { send } from '../lib/api';
import { reportError } from '../lib/client-log';
import QRCode from 'qrcode';

type PayMethod = 'cashu' | 'lightning' | 'nut18' | 'webln';

interface MintOption {
  url: string;
  name: string;
  testnet: boolean;
  errorRate?: number;
  latencyMs?: number | null;
  healthy?: boolean;
}

interface ComposeProps {
  domain: string;
}

export function Compose({ domain: _domain }: ComposeProps) {
  const { composeOpen, setComposeOpen, showToast } = useStore();

  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [showToError, setShowToError] = useState(false);

  const [step, setStep] = useState<'draft' | 'pay'>('draft');
  const [payMethod, setPayMethod] = useState<PayMethod>('lightning');
  const [cashuToken, setCashuToken] = useState('');

  const [statusMsg, setStatusMsg] = useState('');
  const [statusType, setStatusType] = useState<'' | 'success' | 'error'>('');

  const [sending, setSending] = useState(false);

  const [quoteId, setQuoteId] = useState<string | null>(null);
  const [invoice, setInvoice] = useState('');
  const [invoiceCreated, setInvoiceCreated] = useState(false);
  const [invoiceCreating, setInvoiceCreating] = useState(false);
  const [pollStatus, setPollStatus] = useState('Waiting for payment...');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [selectedMint, setSelectedMint] = useState('');
  const [mintOptions, setMintOptions] = useState<MintOption[]>([]);
  const [nut18CreqA, setNut18CreqA] = useState('');
  const [nut18RequestId, setNut18RequestId] = useState<string | null>(null);
  const [hasWebLN, setHasWebLN] = useState(false);

  useEffect(() => {
    setHasWebLN(typeof window !== 'undefined' && !!(window as unknown as { webln?: unknown }).webln);
  }, []);

  const composeFields = useRef({ to, subject, body });
  composeFields.current = { to, subject, body };

  useEffect(() => {
    if (payMethod === 'lightning' && step === 'pay' && !invoiceCreated && !invoiceCreating) {
      void createLightningInvoice();
    }
    if (payMethod === 'cashu' && step === 'pay' && !nut18CreqA && !nut18RequestId) {
      void createNut18Request();
    }
    if (payMethod === 'webln' && step === 'pay' && !invoiceCreated && !invoiceCreating) {
      void payWithWebLN();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payMethod, step, selectedMint]);

  function startFallbackPolling(qId: string) {
    const w = window as unknown as { _lnPoll?: ReturnType<typeof setInterval> };
    if (w._lnPoll) { clearInterval(w._lnPoll); }
    const poll = setInterval(async () => {
      try {
        if (!qId) return;
        const status = await send.quoteStatus(qId);
        if (status.status === 'paid') {
          clearInterval(poll);
          w._lnPoll = undefined;
          setPollStatus('Payment received! Sending email...');
          void doSendLightning(qId);
        }
      } catch {
        // ignore poll errors
      }
    }, 3000);
    w._lnPoll = poll;
  }

  async function payWithWebLN() {
    setInvoiceCreating(true);
    setInvoiceCreated(false);
    try {
      const quoteData = await send.quote(selectedMint || undefined);
      const webln = (window as unknown as { webln?: { sendPayment: (pr: string) => Promise<{ preimage: string }> } }).webln;

      setInvoice(quoteData.invoice);
      setQuoteId(quoteData.quoteId);
      setInvoiceCreating(false);

      if (!webln) {
        showToast('No WebLN wallet found', 'error');
        setPollStatus('No WebLN wallet — scan QR instead');
        setInvoiceCreated(true);
        startFallbackPolling(quoteData.quoteId);
        return;
      }

      setPollStatus('Approve payment in your wallet...');

      try {
        const result = await webln.sendPayment(quoteData.invoice);
        if (result.preimage) {
          setPollStatus('Payment received! Sending email...');
          setInvoiceCreated(true);
          void doSendLightning(quoteData.quoteId);
        } else {
          setPollStatus('No preimage returned — scan QR instead');
          setInvoiceCreated(true);
          startFallbackPolling(quoteData.quoteId);
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        console.error('WebLN sendPayment error:', e);
        reportError({ type: 'webln_sendpayment_error', message: msg, stack: e instanceof Error ? e.stack ?? '' : '', url: window.location.href, timestamp: Date.now() });
        if (msg.includes('rejected') || msg.includes('cancelled') || msg.includes('denied')) {
          setPollStatus('Payment cancelled — scan QR instead');
        } else {
          setPollStatus('Wallet error — scan QR instead');
        }
        setInvoiceCreated(true);
        startFallbackPolling(quoteData.quoteId);
      }
    } catch (e) {
      console.error('WebLN quote fetch error:', e);
      reportError({ type: 'webln_quote_fetch_error', message: e instanceof Error ? e.message : String(e), url: window.location.href, timestamp: Date.now() });
      const msg = e instanceof Error ? e.message : String(e);
      showToast('Failed to create invoice: ' + msg, 'error');
      setInvoiceCreating(false);
    }
  }

  async function createNut18Request() {
    try {
      const res = await fetch('/api/send/nut18-request', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          to: composeFields.current.to.trim(),
          subject: composeFields.current.subject.trim(),
          text: composeFields.current.body.trim(),
          mintUrl: selectedMint || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create request');
      setNut18CreqA(data.creqA);
      setNut18RequestId(data.requestId);

      const poll = setInterval(async () => {
        try {
          if (!data.requestId) return;
          const statusRes = await fetch(`/api/send/nut18/${data.requestId}/status`);
          const statusData = await statusRes.json();
          if (statusData.status === 'paid' || statusData.status === 'consumed') {
            clearInterval(poll);
            setPollStatus('Payment received! Sending email...');
            setStatusMsg('Email sent successfully!');
            setStatusType('success');
            showToast('Email sent from ' + data.from, 'success');
            setTimeout(() => closeCompose(), 1200);
          }
        } catch { /* ignore */ }
      }, 3000);
      (window as unknown as { _lnPoll?: ReturnType<typeof setInterval> })._lnPoll = poll;
    } catch (e) {
      console.error('NUT-18 request error:', e);
      reportError({ type: 'nut18_request_error', message: e instanceof Error ? e.message : String(e), url: window.location.href, timestamp: Date.now() });
      showToast('Failed to create Cashu payment request', 'error');
    }
  }

  useEffect(() => {
    fetch('/api/mints')
      .then(r => r.json())
      .then(data => {
        if (data.mints) {
          setMintOptions(data.mints);
          if (!selectedMint) setSelectedMint('');
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const qrContent = invoice ? `lightning:${invoice}` : nut18CreqA || '';
    if (qrContent) {
      QRCode.toDataURL(qrContent, {
        width: 256,
        margin: 2,
        color: { dark: '#0d1117', light: '#ffffff' },
      }).then(setQrDataUrl).catch(() => setQrDataUrl(''));
    } else {
      setQrDataUrl('');
    }
  }, [invoice, nut18CreqA]);

  useEffect(() => {
    if (!composeOpen) return;
    const replyTo = sessionStorage.getItem('nomail_reply_to');
    const replySubject = sessionStorage.getItem('nomail_reply_subject');
    if (replyTo) {
      setTo(replyTo);
      sessionStorage.removeItem('nomail_reply_to');
    }
    if (replySubject) {
      setSubject(replySubject);
      sessionStorage.removeItem('nomail_reply_subject');
    }
  }, [composeOpen]);

  useEffect(() => {
    if (!composeOpen) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        if (step === 'pay') {
          void doSend();
        } else {
          proceedToPayment();
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composeOpen, step, to, subject, body, cashuToken, payMethod, quoteId]);

  const validateTo = (val: string): boolean =>
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(val);

  const proceedToPayment = useCallback(() => {
    const toVal = to.trim();
    const subjVal = subject.trim();
    const textVal = body.trim();

    let valid = true;
    if (!toVal || !validateTo(toVal)) {
      setShowToError(true);
      valid = false;
    } else {
      setShowToError(false);
    }
    if (!subjVal) {
      showToast('Subject is required', 'error');
      valid = false;
    }
    if (!textVal) {
      showToast('Message body is required', 'error');
      valid = false;
    }
    if (!valid) return;
    setPayMethod(getRecommendedMethod());
    setStep('pay');
  }, [to, subject, body, showToast, hasWebLN]);

  const backToDraft = useCallback(() => {
    setStep('draft');
    setStatusMsg('');
    setStatusType('');
  }, []);

  const handleSwitchPayment = useCallback((method: PayMethod) => {
    const w = window as unknown as { _lnPoll?: ReturnType<typeof setInterval> };
    if (w._lnPoll) { clearInterval(w._lnPoll); w._lnPoll = undefined; }
    setInvoice('');
    setInvoiceCreated(false);
    setInvoiceCreating(false);
    setNut18CreqA('');
    setNut18RequestId(null);
    setQrDataUrl('');
    setPollStatus('Waiting for payment...');
    setPayMethod(method);
  }, []);

  function getRecommendedMethod(): PayMethod {
    // Always default to Lightning — WebLN is available as a button but never auto-selected.
    return 'lightning';
  }

  const handleMintChange = useCallback((mintUrl: string) => {
    const w = window as unknown as { _lnPoll?: ReturnType<typeof setInterval> };
    if (w._lnPoll) { clearInterval(w._lnPoll); w._lnPoll = undefined; }
    setQuoteId(null);
    setInvoice('');
    setInvoiceCreated(false);
    setInvoiceCreating(false);
    setPollStatus('Waiting for payment...');
    setQrDataUrl('');
    setNut18CreqA('');
    setNut18RequestId(null);
    setSelectedMint(mintUrl);
  }, []);

  const createLightningInvoice = useCallback(async () => {
    setInvoiceCreating(true);
    try {
      const data = await send.quote(selectedMint || undefined);
      setQuoteId(data.quoteId);
      setInvoice(data.invoice);
      setInvoiceCreated(true);
      setInvoiceCreating(false);
      setPollStatus('Waiting for payment...');

      const poll = setInterval(async () => {
        try {
          if (!data.quoteId) return;
          const status = await send.quoteStatus(data.quoteId);
          if (status.status === 'paid') {
            clearInterval(poll);
            setPollStatus('Payment received! Sending email...');
            void doSendLightning(data.quoteId);
          }
        } catch {
          // ignore poll errors
        }
      }, 3000);

      // Store interval for cleanup
      (window as unknown as { _lnPoll?: ReturnType<typeof setInterval> })._lnPoll = poll;
    } catch (e) {
      console.error('Invoice creation error:', e);
      reportError({ type: 'invoice_creation_error', message: e instanceof Error ? e.message : String(e), url: window.location.href, timestamp: Date.now() });
      showToast('Failed to create invoice. Please try again.', 'error');
      setInvoiceCreating(false);
    }
  }, [showToast, selectedMint]);

  const copyInvoice = useCallback(() => {
    navigator.clipboard.writeText(invoice).then(() => showToast('Invoice copied!', 'success'));
  }, [invoice, showToast]);

  const doSend = useCallback(async () => {
    if (payMethod === 'lightning') {
      if (!quoteId) {
        showToast('Create a Lightning invoice first', 'error');
        return;
      }
      void doSendLightning(quoteId);
      return;
    }

    const token = cashuToken.trim();
    if (!token) {
      showToast('Paste a Cashu token (100 sats)', 'error');
      return;
    }
    if (!token.startsWith('cashuA') && !token.startsWith('[') && !token.startsWith('{')) {
      showToast('Invalid token format. Cashu tokens start with "cashuA".', 'error');
      return;
    }

    setSending(true);
    setStatusMsg('Sending email...');
    setStatusType('');

    try {
      const data = await send.send(to.trim(), subject.trim(), body.trim(), {
        cashuToken: token,
      });
      setStatusMsg('Email sent successfully!');
      setStatusType('success');
      showToast('Email sent from ' + data.from, 'success');
      setTimeout(() => closeCompose(), 1200);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setStatusMsg(msg);
      setStatusType('error');
      showToast(msg, 'error');
      setSending(false);
    }
  }, [payMethod, quoteId, cashuToken, to, subject, body, showToast]);

  const doSendLightning = useCallback(
    async (qId: string) => {
      const { to: toVal, subject: subjVal, body: bodyVal } = composeFields.current;
      setSending(true);
      setStatusMsg('Sending email...');
      setStatusType('');

      try {
        const data = await send.send(toVal.trim(), subjVal.trim(), bodyVal.trim(), {
          paidQuoteId: qId,
        });
        setStatusMsg('Email sent successfully!');
        setStatusType('success');
        showToast('Email sent from ' + data.from, 'success');
        setTimeout(() => closeCompose(), 1200);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setStatusMsg(msg);
        setStatusType('error');
        showToast(msg, 'error');
        setSending(false);
      }
    },
    [showToast],
  );

  const closeCompose = useCallback(() => {
    const hasContent = to.trim() || subject.trim() || body.trim();
    if (hasContent && step === 'draft') {
      if (!confirm('Discard this email?')) return;
    }
    setComposeOpen(false);
    setStep('draft');
    setTo('');
    setSubject('');
    setBody('');
    setCashuToken('');
    setShowToError(false);
    setStatusMsg('');
    setStatusType('');
    setPayMethod('lightning');
    setQuoteId(null);
    setInvoice('');
    setInvoiceCreated(false);
    setInvoiceCreating(false);
    setPollStatus('Waiting for payment...');
    setQrDataUrl('');
    setNut18CreqA('');
    setNut18RequestId(null);
    setSending(false);

    const w = window as unknown as { _lnPoll?: ReturnType<typeof setInterval> };
    if (w._lnPoll) {
      clearInterval(w._lnPoll);
      w._lnPoll = undefined;
    }
  }, [to, subject, body, step, setComposeOpen]);

  if (!composeOpen) return null;

  return (
    <div
      class="compose-overlay active"
      role="dialog"
      aria-modal="true"
      aria-label="Compose email"
      data-testid="compose-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) closeCompose();
      }}
    >
      <div class="compose-card">
        <h2>
          Compose Email <span class="compose-price">(100 sats)</span>
        </h2>
        <p class="compose-hint">Sending costs 100 sats (~$0.10) as anti-spam postage.</p>

        {step === 'draft' && (
          <div class="compose-step" id="compose-draft">
            <div class="field compose-field">
              <label>To</label>
              <input
                type="text"
                placeholder="recipient@example.com"
                value={to}
                class={showToError ? 'invalid' : ''}
                onInput={(e) => {
                  const val = (e.target as HTMLInputElement).value;
                  setTo(val);
                  if (val.length > 0) setShowToError(!validateTo(val));
                }}
                data-testid="compose-to"
              />
              {showToError && (
                <div class="compose-field-error show">
                  Please enter a valid email address
                </div>
              )}
            </div>
            <div class="field">
              <label>Subject</label>
              <input
                type="text"
                placeholder="Subject"
                value={subject}
                onInput={(e) => setSubject((e.target as HTMLInputElement).value)}
                data-testid="compose-subject"
              />
            </div>
            <div class="field">
              <label>Message</label>
              <textarea
                class="body-input"
                placeholder="Write your message..."
                value={body}
                onInput={(e) => setBody((e.target as HTMLTextAreaElement).value)}
                data-testid="compose-body"
              />
            </div>
            <div class="compose-actions">
              <button class="btn btn-ghost" onClick={closeCompose}>
                Cancel
              </button>
              <button class="btn" onClick={proceedToPayment} data-testid="compose-proceed-btn">
                Pay &amp; Send
              </button>
            </div>
            <div class="send-hint">
              Press <strong>Cmd/Ctrl+Enter</strong> to continue
            </div>
          </div>
        )}

        {step === 'pay' && (
          <div class="compose-pay-section active" id="compose-pay">
            <div class="compose-summary">
              <strong>To:</strong> {to.trim()}
              <br />
              <strong>Subject:</strong> {subject.trim()}
              <br />
              <strong>Length:</strong> {body.trim().length} chars
            </div>

            <div class="field">
              <label>Payment Method</label>
              <div class="pay-toggle">
                {hasWebLN && (
                  <button
                    class={'btn btn-sm pay-opt' + (payMethod === 'webln' ? ' active' : ' btn-ghost')}
                    onClick={() => handleSwitchPayment('webln')}
                  >
                    WebLN
                  </button>
                )}
                <button
                  class={'btn btn-sm pay-opt' + (payMethod === 'lightning' ? ' active' : ' btn-ghost')}
                  onClick={() => handleSwitchPayment('lightning')}
                >
                  Lightning
                </button>
                <button
                  class={'btn btn-sm pay-opt' + (payMethod === 'cashu' ? ' active' : ' btn-ghost')}
                  onClick={() => handleSwitchPayment('cashu')}
                >
                  Cashu
                </button>
              </div>
            </div>

            {payMethod === 'webln' && (
              <div class="field" id="webln-pay-section">
                {invoiceCreating ? (
                  <div style="text-align:center;padding:24px;color:var(--muted)">
                    Connecting to wallet...
                  </div>
                ) : invoiceCreated && invoice && (pollStatus.includes('cancelled') || pollStatus.includes('error') || pollStatus.includes('scan QR')) ? (
                  <>
                    <label>{pollStatus}</label>
                    <div style="display:flex;flex-direction:column;align-items:center;gap:12px;padding:12px 0">
                      {qrDataUrl && (
                        <a href={`lightning:${invoice}`} target="_blank" rel="noopener" style="line-height:0">
                          <img
                            src={qrDataUrl}
                            alt="Payment QR code"
                            style="width:220px;height:220px;border-radius:8px;border:1px solid var(--border)"
                            data-testid="webln-qr"
                          />
                        </a>
                      )}
                      <div style="display:flex;gap:8px;flex-wrap:wrap;justify-content:center">
                        <button class="btn btn-sm" onClick={() => void payWithWebLN()} data-testid="webln-retry-btn">
                          Pay with wallet
                        </button>
                        <a
                          class="btn btn-sm"
                          href={`lightning:${invoice}`}
                          data-testid="webln-lightning-link"
                        >
                          Open Wallet
                        </a>
                        <button
                          class="btn btn-sm btn-ghost"
                          onClick={copyInvoice}
                          data-testid="webln-copy-invoice-btn"
                        >
                          Copy Invoice
                        </button>
                      </div>
                    </div>
                  </>
                ) : invoiceCreated ? (
                  <div style="display:flex;flex-direction:column;align-items:center;gap:12px;padding:12px 0">
                    <div style="font-size:14px;color:var(--text)">{pollStatus}</div>
                  </div>
                ) : invoice ? (
                  <div style="display:flex;flex-direction:column;align-items:center;gap:12px;padding:12px 0">
                    <div style="font-size:14px;color:var(--text)">
                      {pollStatus || 'Approve payment in your wallet...'}
                    </div>
                  </div>
                ) : (
                  <button class="btn" style="width:100%" onClick={() => void payWithWebLN()} data-testid="webln-pay-btn">
                    Pay with Lightning Wallet
                  </button>
                )}
              </div>
            )}

            {(payMethod === 'lightning' || payMethod === 'webln') && (
              <div class="field">
                <label>Mint</label>
                <select
                  class="mint-select"
                  value={selectedMint}
                  onChange={(e) => handleMintChange((e.target as HTMLSelectElement).value)}
                  data-testid="mint-select"
                >
                  <option value="">Auto (best)</option>
                  {mintOptions.map(m => (
                    <option key={m.url} value={m.url}>
                      {m.errorRate !== undefined && m.errorRate < 0.03 ? '🟢 '
                        : m.errorRate !== undefined && m.errorRate < 0.08 ? '🟡 '
                        : m.errorRate !== undefined ? '🔴 '
                        : ''}
                      {m.name}
                      {m.errorRate !== undefined ? ` (${(m.errorRate * 100).toFixed(1)}% err)` : ''}
                      {m.testnet ? ' (free)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {payMethod === 'cashu' && (
              <div class="field" id="cashu-pay-section">
                <label>
                  Cashu Token <span class="compose-price">— 100 sats required</span>
                </label>
                <textarea
                  class="token-input"
                  placeholder="Paste Cashu token (cashuA...)"
                  value={cashuToken}
                  onInput={(e) => setCashuToken((e.target as HTMLTextAreaElement).value)}
                  data-testid="cashu-token-input"
                />
                <div style="margin-top:12px;padding-top:12px;border-top:1px solid var(--border)">
                  <div style="font-size:13px;color:var(--muted);margin-bottom:8px">
                    Or scan with your Cashu wallet:
                  </div>
                  {nut18CreqA && qrDataUrl ? (
                    <div style="display:flex;flex-direction:column;align-items:center;gap:8px">
                      <img src={qrDataUrl} alt="Cashu payment QR" style="width:180px;height:180px;border-radius:8px;border:1px solid var(--border)" data-testid="nut18-qr" />
                      <div style="font-size:12px;color:var(--muted);text-align:center" data-testid="nut18-status">
                        Scan with any Cashu wallet — any mint accepted
                      </div>
                    </div>
                  ) : (
                    <div style="font-size:12px;color:var(--muted);text-align:center;padding:8px">
                      Generating Cashu payment request...
                    </div>
                  )}
                </div>
              </div>
            )}

            {payMethod === 'lightning' && (
              <div class="field" id="lightning-pay-section">
                {invoiceCreating ? (
                  <div style="text-align:center;padding:24px;color:var(--muted)">
                    Creating invoice...
                  </div>
                ) : invoiceCreated && invoice ? (
                  <>
                    <label>
                      Scan to pay <span class="compose-price">— 100 sats</span>
                    </label>
                    <div style="display:flex;flex-direction:column;align-items:center;gap:12px;padding:12px 0">
                      {qrDataUrl && (
                        <a href={`lightning:${invoice}`} target="_blank" rel="noopener" style="line-height:0">
                          <img
                            src={qrDataUrl}
                            alt="Payment QR code"
                            style="width:220px;height:220px;border-radius:8px;border:1px solid var(--border)"
                            data-testid="payment-qr"
                          />
                        </a>
                      )}
                      <div style="display:flex;gap:8px">
                        <a
                          class="btn btn-sm"
                          href={`lightning:${invoice}`}
                          data-testid="lightning-link"
                        >
                          Open Wallet
                        </a>
                        <button
                          class="btn btn-sm btn-ghost"
                          onClick={copyInvoice}
                          data-testid="copy-invoice-btn"
                        >
                          Copy Invoice
                        </button>
                      </div>
                      <div style="font-size:13px;color:var(--muted);text-align:center" data-testid="poll-status">
                        {pollStatus}
                      </div>
                    </div>
                  </>
                ) : (
                  <div style="text-align:center;padding:12px;color:var(--muted)">
                    Click "Lightning" again to generate an invoice.
                  </div>
                )}
              </div>
            )}

            {statusMsg && (
              <div class={'compose-status show ' + statusType}>{statusMsg}</div>
            )}

            <div class="compose-actions">
              <button class="btn btn-ghost" onClick={backToDraft} disabled={sending} data-testid="compose-back-btn">
                Back
              </button>
              <button class="btn" onClick={() => void doSend()} disabled={sending} data-testid="compose-send-btn">
                {sending ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
